import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : import parts
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Segment, Icon, List, Label, Checkbox } from 'semantic-ui-react';
// ---Framework imports
import { Actions, Sel } from 'uix/registries';
import log from 'uix/logging';
import { keys, dict, sorted, mapItems, groupBy, sum, isNil, callIfFunc, isNumber } from 'ldash';
import { useState, useEffect } from 'uix/hooks';
import { prettyBytes } from 'uix/utils';
// ---Local imports
// ---Exports
let pathsToTree = (paths)=>{
    let result = [];
    let level = {
        result
    };
    paths = sorted(paths, (p)=>{
        let parts = p.split('/');
        return [
            parts.length,
            parts.slice(-1)[0]
        ];
    });
    paths.forEach((path)=>{
        let parts = path.split('/');
        parts.reduce((r, name, i)=>{
            if (!r[name]) {
                r[name] = {
                    result: []
                };
                let p = {
                    name
                };
                let isRawDir = path.endsWith('/');
                if (i == parts.length - 1 && !isRawDir) {
                    p = {
                        ...p,
                        isFile: true,
                        path
                    };
                } else {
                    // readding slash is important for directory-focused id (path)
                    p = {
                        ...p,
                        children: r[name].result,
                        path: parts.slice(0, i + 1).join('/') + '/'
                    };
                }
                r.result.push(p);
            }
            return r[name];
        }, level);
    });
    return result;
};
let defaultRenderFileName = (fn, file)=>fn;
export const DirectoryTree = ({ // list if dicts with shape [{path, size}, ...]
// a path ending with '/' will be handled as an directory
files, collapseFiles = false, renderFileName = defaultRenderFileName, renderFileStatus = undefined, attached = false, selectable = false, externalSelected, onSelectionChange, focusable = false, onFocusChange })=>{
    let paths = dict(files.map((f)=>[
            f.path,
            f
        ]));
    let tree = pathsToTree(keys(paths));
    let [focused, setFocused] = useState(null);
    if (!paths[focused]) {
        focused = null;
    }
    useEffect(()=>{
        callIfFunc(onFocusChange, focused);
    }, [
        focused,
        setFocused
    ]);
    let [selected, setSelected] = useState([]);
    selected = externalSelected || selected;
    let toggleSelected = (node, checked)=>{
        let nextSelected = checked ? selected.filter((path)=>path != node.path) : [
            ...selected,
            node.path
        ];
        setSelected(nextSelected);
        if (onSelectionChange) {
            onSelectionChange(nextSelected);
        }
    };
    const NodeCheckbox = ({ node })=>{
        let checked = selected.includes(node.path);
        return /*#__PURE__*/ _jsx(Checkbox, {
            label: node.name,
            checked: checked,
            onChange: ()=>toggleSelected(node, checked)
        });
    };
    let buildDirectoryHeader = (node, level)=>{
        if (!selectable || level > 1) {
            return node.name;
        } else {
            return /*#__PURE__*/ _jsx(NodeCheckbox, {
                node: node
            });
        }
    };
    let focusProps = (n)=>({
            onClick: focusable ? ()=>setFocused(n.path) : null,
            as: focusable ? 'a' : null,
            style: {
                backgroundColor: focused == n.path ? 'var(--vl_accent_color-hover)' : null
            }
        });
    let recurse = (node, level = 0)=>{
        let children = node.children.map((n)=>{
            if (n.isFile) {
                let filedata = paths[n.path];
                let isFirstLevelFile = level == 0;
                if (collapseFiles && !isFirstLevelFile) return null;
                let file = renderFileName(n.name, filedata);
                if (isFirstLevelFile && selectable) {
                    file = /*#__PURE__*/ _jsx(NodeCheckbox, {
                        node: n
                    });
                }
                let status = null;
                if (renderFileStatus) {
                    status = renderFileStatus(n.name, filedata);
                }
                return /*#__PURE__*/ _jsxs(List.Item, {
                    active: true,
                    ...focusProps(n),
                    children: [
                        /*#__PURE__*/ _jsxs(List.Content, {
                            floated: "right",
                            children: [
                                prettyBytes(filedata.size || 0),
                                " ",
                                status
                            ]
                        }),
                        /*#__PURE__*/ _jsx(List.Icon, {
                            name: "file outline"
                        }),
                        /*#__PURE__*/ _jsx(List.Content, {
                            children: /*#__PURE__*/ _jsx(List.Header, {
                                children: file
                            })
                        })
                    ]
                }, n.name);
            } else {
                return recurse(n, level + 1);
            }
        });
        if (node.name == '') {
            // build no item for root node
            return children;
        }
        let byExt = groupBy(node.children.filter((n)=>n.isFile), (n)=>n.name.split('.').slice(-1)[0]);
        let summary = mapItems(byExt, (ext, files)=>{
            let size = prettyBytes(sum(files.map((f)=>paths[f.path].size)) || 0);
            return /*#__PURE__*/ _jsx(Label, {
                basic: true,
                style: {
                    padding: '0.1em 0.633em'
                },
                title: size,
                children: `${ext}: ${files.length}`
            }, ext);
        });
        let num_dirs = node.children.filter((n)=>!n.isFile).length;
        let dirdata = paths[node.path];
        return /*#__PURE__*/ _jsxs(List.Item, {
            ...focusProps(node),
            children: [
                isNumber(dirdata?.size) && /*#__PURE__*/ _jsx(List.Content, {
                    floated: "right",
                    children: prettyBytes(dirdata.size)
                }),
                /*#__PURE__*/ _jsx(List.Icon, {
                    name: "folder outline"
                }),
                /*#__PURE__*/ _jsxs(List.Content, {
                    children: [
                        /*#__PURE__*/ _jsxs(List.Header, {
                            children: [
                                collapseFiles && summary.length > 0 && /*#__PURE__*/ _jsx("span", {
                                    style: {
                                        float: 'right'
                                    },
                                    children: summary
                                }),
                                buildDirectoryHeader(node, level)
                            ]
                        }),
                        !collapseFiles || num_dirs > 0 ? /*#__PURE__*/ _jsx(List.List, {
                            style: {
                                padding: '0.20em 0em 0.20em 0.5em',
                                width: '100%'
                            },
                            children: children
                        }) : null
                    ]
                })
            ]
        }, node.name);
    };
    let content = null;
    if (tree[0]) {
        if (tree[0].children) {
            content = /*#__PURE__*/ _jsx(List, {
                size: collapseFiles ? 'large' : 'tiny',
                children: recurse(tree[0])
            });
        } else {
            content = 'no children';
        }
    }
    return /*#__PURE__*/ _jsx(Segment, {
        attached: attached,
        style: {
            flex: 1,
            overflow: 'auto'
        },
        children: content
    });
};
